import { ensureFontsForElements, resolveFileUrl } from './fonts';
import { SHAPES, lineDash } from './shapes';
import { charColors } from './richText';

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = resolveFileUrl(src);
  });

const roundRectPath = (ctx, x, y, w, h, r) => {
  const rad = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
};

const wrapText = (ctx, text, maxWidth) => {
  const fits = (t) => ctx.measureText(t).width <= maxWidth;
  const breakWord = (word) => {
    const parts = [];
    let chunk = '';
    for (const ch of word) {
      if (chunk && !fits(chunk + ch)) { parts.push(chunk); chunk = ch; } else chunk += ch;
    }
    if (chunk) parts.push(chunk);
    return parts;
  };
  const lines = [];
  text.split('\n').forEach((para) => {
    let line = '';
    para.split(' ').forEach((word) => {
      const test = line ? `${line} ${word}` : word;
      if (fits(test)) { line = test; return; }
      if (line) lines.push(line);
      if (fits(word)) { line = word; return; }
      const parts = breakWord(word);
      lines.push(...parts.slice(0, -1));
      line = parts[parts.length - 1] || '';
    });
    lines.push(line);
  });
  return lines;
};

const drawText = (ctx, el, s) => {
  const size = (el.fontSize || 24) * s;
  const family = el.fontFamily || 'Manrope';
  ctx.font = `${el.fontStyle === 'italic' ? 'italic ' : ''}${el.fontWeight || 400} ${size}px '${family}', sans-serif`;
  ctx.fillStyle = el.color || '#ffffff';
  ctx.textBaseline = 'top';
  if ('letterSpacing' in ctx) ctx.letterSpacing = `${(el.letterSpacing || 0) * s}px`;
  if (el.shadow) { ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 8 * s; ctx.shadowOffsetY = 2 * s; }
  const text = el.uppercase ? (el.text || '').toUpperCase() : el.text || '';
  const lh = size * (el.lineHeight || 1.2);
  const lines = wrapText(ctx, text, el.w * s);
  const w = el.w * s;
  const base = el.color || '#ffffff';
  let colors = charColors(el, !!el.uppercase);
  if (colors && colors.length !== text.length) colors = null;
  let ptr = 0;
  lines.forEach((line, i) => {
    const lw = ctx.measureText(line).width;
    const x0 = el.align === 'center' ? (w - lw) / 2 : el.align === 'right' ? w - lw : 0;
    const y = i * lh + (lh - size) / 2;
    if (!colors) { ctx.fillText(line, x0, y); return; }
    const found = line ? text.indexOf(line, ptr) : -1;
    const start = found < 0 ? ptr : found;
    let x = x0;
    let seg = '';
    let segColor = colors[start] ?? null;
    const flush = () => {
      if (!seg) return;
      ctx.fillStyle = segColor || base;
      ctx.fillText(seg, x, y);
      x += ctx.measureText(seg).width;
      seg = '';
    };
    for (let k = 0; k < line.length; k += 1) {
      const c = colors[start + k] ?? null;
      if (c !== segColor) { flush(); segColor = c; }
      seg += line[k];
    }
    flush();
    ptr = start + line.length;
  });
};

const drawImage = (ctx, el, s, img) => {
  const w = el.w * s;
  const h = el.h * s;
  roundRectPath(ctx, 0, 0, w, h, (el.radius || 0) * s);
  ctx.save();
  ctx.clip();
  if (!img) {
    ctx.fillStyle = '#27272a';
    ctx.fillRect(0, 0, w, h);
  } else if (el.fit === 'fill') {
    ctx.drawImage(img, 0, 0, w, h);
  } else {
    const ir = img.naturalWidth / img.naturalHeight;
    const br = w / h;
    const cover = (el.fit || 'cover') === 'cover';
    let dw = w, dh = h;
    if (cover ? ir > br : ir < br) { dw = h * ir; dh = h; } else { dw = w; dh = w / ir; }
    if (cover) ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    else ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
  }
  ctx.restore();
};

const drawShape = (ctx, el, s) => {
  const w = el.w * s;
  const h = el.h * s;
  const sw = (el.strokeWidth || 0) * s;
  const path = (inset) => {
    if (el.type === 'ellipse') {
      ctx.beginPath();
      ctx.ellipse(w / 2, h / 2, Math.max(0, w / 2 - inset), Math.max(0, h / 2 - inset), 0, 0, Math.PI * 2);
    } else roundRectPath(ctx, inset, inset, w - inset * 2, h - inset * 2, (el.radius || 0) * s - inset);
  };
  path(0);
  ctx.fillStyle = el.fill || '#A855F7';
  ctx.fill();
  if (sw > 0) {
    path(sw / 2);
    ctx.lineWidth = sw;
    ctx.strokeStyle = el.stroke || '#ffffff';
    ctx.stroke();
  }
};

const drawLine = (ctx, el, s) => {
  const w = el.w * s;
  const h = el.h * s;
  const sw = (el.strokeWidth || 3) * s;
  ctx.beginPath();
  ctx.moveTo(0, h / 2);
  ctx.lineTo(w, h / 2);
  ctx.lineWidth = sw;
  ctx.strokeStyle = el.stroke || '#ffffff';
  ctx.lineCap = el.dash === 'dotted' ? 'round' : el.cap || 'round';
  ctx.setLineDash(lineDash(el.dash, sw));
  ctx.stroke();
  ctx.setLineDash([]);
};

const drawPathShape = (ctx, el, s) => {
  const def = SHAPES[el.shape] || SHAPES.triangle;
  const w = el.w * s;
  const h = el.h * s;
  const path = new Path2D(def.path);
  ctx.save();
  ctx.scale(w / 100, h / 100);
  ctx.fillStyle = el.fill || '#A855F7';
  ctx.fill(path, def.evenOdd ? 'evenodd' : 'nonzero');
  ctx.restore();
  const sw = (el.strokeWidth || 0) * s;
  if (sw > 0) {
    ctx.save();
    ctx.scale(w / 100, h / 100);
    ctx.lineWidth = sw / ((w / 100 + h / 100) / 2);
    ctx.lineJoin = 'round';
    ctx.strokeStyle = el.stroke || '#ffffff';
    ctx.stroke(path);
    ctx.restore();
  }
};

export const renderMenuToCanvas = async (menu, customFonts = [], scale = 3) => {
  const cv = menu.canvas || { width: 390, height: 844, background: '#09090b' };
  const elements = (menu.elements || []).filter((e) => !e.hidden);
  ensureFontsForElements(elements, customFonts);
  await Promise.all(
    elements.filter((e) => e.type === 'text').map((e) =>
      document.fonts.load(`${e.fontStyle === 'italic' ? 'italic ' : ''}${e.fontWeight || 400} ${e.fontSize || 24}px '${e.fontFamily || 'Manrope'}'`).catch(() => null)
    )
  );
  await document.fonts.ready;
  const images = await Promise.all(elements.map((e) => (e.type === 'image' && e.src ? loadImage(e.src) : Promise.resolve(null))));

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(cv.width * scale);
  canvas.height = Math.round(cv.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = cv.background || '#09090b';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  elements.forEach((el, i) => {
    ctx.save();
    ctx.globalAlpha = el.opacity ?? 1;
    const w = el.w * scale;
    const h = el.h * scale;
    ctx.translate(el.x * scale + w / 2, el.y * scale + h / 2);
    ctx.rotate(((el.rotation || 0) * Math.PI) / 180);
    ctx.translate(-w / 2, -h / 2);
    if (el.type === 'text') drawText(ctx, el, scale);
    else if (el.type === 'image') drawImage(ctx, el, scale, images[i]);
    else if (el.type === 'line') drawLine(ctx, el, scale);
    else if (el.type === 'shape') drawPathShape(ctx, el, scale);
    else drawShape(ctx, el, scale);
    ctx.restore();
  });
  return canvas;
};

const safeName = (title) => (title || 'menu').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'menu';

export const downloadMenuJpeg = async (menu, customFonts = []) => {
  const canvas = await renderMenuToCanvas(menu, customFonts, 3);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
  if (!blob) throw new Error('Could not render image');
  const filename = `${safeName(menu.title)}.jpg`;
  const file = new File([blob], filename, { type: 'image/jpeg' });
  const isTouch = window.matchMedia('(pointer: coarse)').matches;
  if (isTouch && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: menu.title });
      return 'shared';
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled';
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return 'downloaded';
};
